import { chakra, Flex, GridProps, useColorModeValue } from '@chakra-ui/react';
import { isNonNullable } from '@main/shared/utils';

import { selectValueToArray } from '../combobox';
import { EditableMultiSelectAvatar, GroupedAvatars } from '../editable/editable-multiselect-avatar';
import { NavigateLink, type NavigateToDrawer, type NavigateToRoute } from '../navigate-link';
import { OverflowContainer } from '../overflow-tooltip';
import { Card, CardActionsProps, CardTagsProps } from './base-card';

interface ReferenceCardProps extends CardActionsProps, CardTagsProps {
  size?: 'sm' | 'md';
  owners?: (string | undefined)[];
  name: string | undefined | null;
  isReadOnly?: boolean;
  cardProps?: GridProps;
  navigate?: NavigateToDrawer | NavigateToRoute;
}

export const ReferenceCard = ({
  size,
  name,
  owners: _owners,
  tags,
  actions,
  isReadOnly,
  numberOfInlineActions,
  navigate,
  cardProps,
}: ReferenceCardProps) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const owners = _owners?.filter(isNonNullable);
  const nameContent = (
    <chakra.p
      noOfLines={1}
      fontSize="sm"
      fontWeight="medium"
      lineHeight={7}
      color={textColor}
      /* Code below important to imitate card accessibility on focus */
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
      }}
      _focus={{
        ring: 0,
        _before: {
          ring: '3px',
          ringColor: 'rgba(66, 153, 225, 0.6)',
          rounded: 'lg',
        },
      }}
      _active={{
        _before: {
          ring: 0,
        },
      }}
      _hover={{
        textDecoration: 'none',
      }}
    >
      {name}
    </chakra.p>
  );

  //TODO: Update to accept AvatarOptionProps when we have avatar images
  const ownersAvatarOptions = selectValueToArray(
    owners?.map((owner, idx) => ({ displayName: owner, id: idx.toString() })),
  );

  return (
    <Card
      size={size}
      position="relative"
      aria-label={
        navigate &&
        ('ariaLabel' in navigate
          ? navigate.ariaLabel
          : 'drawerEntity' in navigate
            ? navigate.drawerEntity
            : undefined)
      }
      isReadOnly={isReadOnly}
      {...cardProps}
    >
      <Card.Body>
        <OverflowContainer>
          <OverflowContainer.Tooltip label={name} placement="top" gutter={12} openDelay={500}>
            {navigate ? (
              <NavigateLink navigate={navigate}>{nameContent}</NavigateLink>
            ) : (
              nameContent
            )}
          </OverflowContainer.Tooltip>
        </OverflowContainer>
      </Card.Body>

      <Card.Header
        left={<Card.Tags tags={tags} />}
        right={
          owners && (
            <Flex gap={4} alignItems="center">
              <GroupedAvatars values={ownersAvatarOptions} groupSize={2} size="xs" variant="short">
                {selectValueToArray(ownersAvatarOptions).map((data, idx) => (
                  <EditableMultiSelectAvatar.Option key={idx} size="xs" {...data} />
                ))}
              </GroupedAvatars>
            </Flex>
          )
        }
        actions={actions}
        numberOfInlineActions={numberOfInlineActions}
      />
    </Card>
  );
};
